// NodeDateRangeModal.jsx

import React, { useEffect, useRef, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { normalizeRangeForSave } from 'node/form/fields/utilities/dateFieldUtils';

// CSS
import './NodeDateRangeModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import NodeDateRangeInput from 'node/form/fields/daterange/NodeDateRangeInput';

/**
 * NodeDateRangeModal
 *
 * Modal editor for schema-driven date range fields.
 *
 * @param {object} value - Current field value.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onChange - Callback when the value is saved.
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeDateRangeModal = ({ value, isOpen, setOpen, onChange }) => {

  // ----- STATE ----- //

  const [dateRange, setDateRange] = useState(null);

  // ----- REFS ----- //

  const dateRangeRef = useRef(null);

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- EFFECTS ----- //

  // When modal opens, initialize local date range state from the provided value.

  useEffect(() => {
    if (!isOpen) return;
    const nextValue = value ? { ...value } : null;
    dateRangeRef.current = nextValue;
    setDateRange(nextValue);
  }, [isOpen, value]);

  // ----- HANDLERS ----- //

  /**
   * Handles changes to the date range from the input component, updating both local state and ref.
   *
   * @param {object|null} nextValue - The next date range value emitted by the input component.
   */

  const handleDateRangeChange = (nextValue) => {
    dateRangeRef.current = nextValue;
    setDateRange(nextValue);
  };

  /**
   * Handles saving the date range when the user clicks the save button. Validates that a 
   * start date is present, and then calls the onChange callback with the normalized value.
   */
  const handleSave = () => {

    // Blur any active element to ensure any in-progress input is finalized before saving.
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Normalize the date range value for saving. If no start date is present, 
    // consider the range cleared.
    const normalized = normalizeRangeForSave(dateRangeRef.current ?? dateRange, 'date');
    if (!normalized?.startDate) {
      alert('Start date is required.');
      return;
    }

    // Emit the change with the normalized value.
    if (typeof onChange === 'function') {
      onChange(normalized);
    }

    // Close the modal after saving.
    setOpen(false);
  };

  // ----- RENDER ----- //

  return (

    <Modal
      title="Date Range"
      isOpen={isOpen}
      width={isMobile ? '100%' : 560}
      height={300}
      onClose={() => setOpen(false)}
    >
      <ModalForm>

        {/* WRAPPER */}
        <div className="node-date-range-modal-wrapper">
          <NodeDateRangeInput
            value={dateRange}
            hybrid={false}
            autoFillEnd={false}
            onChange={handleDateRangeChange}
          />
        </div>
      </ModalForm>

      <ModalButtons>
        <Button label="Save" onClick={handleSave} />
      </ModalButtons>

    </Modal>

  );

};

// Dev Tools
NodeDateRangeModal.displayName = 'NodeDateRangeModal';

export default NodeDateRangeModal;
